import React, { useState, useEffect } from 'react';
import Dialog from '../Dialog/Dialog';
import { TextButton } from '../Button';
import { supabase } from '../../lib/supabase';
import './SelectReviewDialog.css';

interface Review {
  id: string;
  title: string;
  description: string;
  status: string;
  created_at: string;
}

interface SelectReviewDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (reviewId: string) => Promise<void>;
  projectId: string;
  isLoading?: boolean;
}

const SelectReviewDialog: React.FC<SelectReviewDialogProps> = ({
  isOpen,
  onClose,
  onSelect,
  projectId,
  isLoading = false
}) => {
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadReviews = async () => {
      if (!isOpen) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const { data, error } = await supabase
          .from('Reviews')
          .select('*')
          .eq('project_id', projectId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setReviews(data || []);
      } catch (err) {
        console.error('Error loading reviews:', err);
        setError(err instanceof Error ? err.message : 'Failed to load reviews');
      } finally {
        setLoading(false);
      }
    };

    loadReviews();
  }, [isOpen, projectId]);

  const handleReviewSelect = async (reviewId: string) => {
    try {
      await onSelect(reviewId);
      onClose();
    } catch (err) {
      console.error('Error adding keys to review:', err);
      setError(err instanceof Error ? err.message : 'Failed to add keys to review');
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Add to Review"
      showCloseButton={false}
      headerActions={
        <TextButton 
          variant="secondary" 
          onClick={onClose}
          size="small"
          disabled={isLoading}
        >
          Cancel
        </TextButton>
      }
    >
      <div className="select-review-dialog-content">
        {loading ? (
          <div className="select-review-dialog-loading">
            <div className="select-review-dialog-spinner"></div>
          </div>
        ) : error ? (
          <div className="select-review-dialog-error">{error}</div>
        ) : reviews.length === 0 ? (
          <div className="select-review-dialog-empty">No reviews found</div>
        ) : (
          <div className="select-review-dialog-list">
            {reviews.map(review => (
              <div 
                key={review.id} 
                className="select-review-dialog-item"
                onClick={() => handleReviewSelect(review.id)}
              >
                <div className="select-review-dialog-item-inset">
                  <div className="select-review-dialog-content-wrapper">
                    <div className="select-review-dialog-title">{review.title}</div>
                    <span className="select-review-dialog-date">
                      Created {new Date(review.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default SelectReviewDialog; 